import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  type DraftFill,
  type DraftFills,
  type DraftSlots,
  FILL_STYLE,
  type PlacedSection,
  fillDraft,
  markSlots,
  sectionTop,
} from "./draft_fill";
import { useFollowTail } from "./use_follow_tail";
import { SETUP_SLOT_STYLE } from "./use_setup_slots";

// The frame the clinician's Word draft is drawn in. Its content comes from his
// file, so it runs no scripts (the sandbox allows only reading it from here),
// and its policy blocks every request except the file's own embedded images
// and fonts, which the viewer turns into blob: URLs. The policy is in <head>,
// ahead of the styles the viewer writes into the body. The browser's scroll
// anchoring is off: a refill replaces the paragraphs it would anchor to, so it
// jumped the page, and the pane keeps the line being written in view itself.
export const DRAFT_FRAME_SANDBOX = "allow-same-origin";
export const DRAFT_FRAME_POLICY =
  "default-src 'none'; style-src 'unsafe-inline'; img-src blob: data:; font-src blob: data:";
export const DRAFT_FRAME_DOCUMENT = `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${DRAFT_FRAME_POLICY}"><style>html { overflow-anchor: none; } body { margin: 0; background: transparent; } body .docx-wrapper { background: transparent; padding: 0; } body .docx-wrapper > section.docx { box-shadow: none; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 20px; } ${FILL_STYLE} ${SETUP_SLOT_STYLE}</style></head><body><div id="draft-styles"></div><div id="draft-document"></div></body></html>`;

type FrameState = "loading" | "ready" | "failed";

// A page's border on both sides.
const FIT_GUTTER = 2;

// Draws the display copy into the frame once per `fileId`, then types the
// sections in at their markers on every change; while `filling`, the growing
// section stays in view until he scrolls away. `writing`: the server's marker.
export const useDraftFrame = ({
  frameRef,
  fileId,
  url,
  sections,
  slots,
  writing,
  filling,
}: {
  frameRef: RefObject<HTMLIFrameElement | null>;
  fileId: string | undefined;
  url: string | undefined;
  sections: ReadonlyArray<DraftFill>;
  slots: DraftSlots;
  writing: number | null | undefined;
  filling: boolean;
}) => {
  const [state, setState] = useState<FrameState>("loading");
  const [root, setRoot] = useState<Element | null>(null);
  const fills = useRef<DraftFills>(new Map());
  const urlRef = useRef(url);
  urlRef.current = url;

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !fileId) return;

    let cancelled = false;
    setState("loading");
    setRoot(null);
    fills.current.clear();

    const draw = async () => {
      const doc = frame.contentDocument;
      const body = doc?.getElementById("draft-document");
      const styles = doc?.getElementById("draft-styles");
      const source = urlRef.current;
      if (!body || !styles || !source) return;

      try {
        const response = await fetch(source);
        if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
        const blob = await response.blob();
        const { renderAsync } = await import("docx-preview");
        await renderAsync(blob, body, styles, {
          className: "docx",
          inWrapper: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
        });
        if (cancelled) return;
        setRoot(body);
        setState("ready");
      } catch {
        if (!cancelled) setState("failed");
      }
    };

    if (frame.contentDocument?.getElementById("draft-document")) void draw();
    else frame.addEventListener("load", () => void draw(), { once: true });

    return () => {
      cancelled = true;
    };
  }, [frameRef, fileId]);

  // Pages shrink to the pane's width, never grow past their own.
  useEffect(() => {
    const frame = frameRef.current;
    const wrapper = root?.querySelector<HTMLElement>(".docx-wrapper");
    const sheet = wrapper?.querySelector<HTMLElement>("section.docx");
    if (!frame || !wrapper || !sheet) return;

    const fit = () => {
      const width = sheet.offsetWidth + FIT_GUTTER;
      wrapper.style.zoom = String(Math.min(1, frame.clientWidth / width));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frameRef, root]);

  const placed = useRef(new Map<number, PlacedSection>());
  const tail = useFollowTail(() => frameRef.current?.contentWindow ?? null);
  const startedAt = useRef<{ root: Element; marker: number } | null>(null);
  const newest = writing ?? undefined;
  // Before the layers over the Report read its blocks: their effects run first.
  useLayoutEffect(() => {
    if (!root) return;

    placed.current = fillDraft(root, sections, fills.current);
    markSlots(root, slots);
    const section =
      newest === undefined ? undefined : placed.current.get(newest);
    if (!filling) {
      startedAt.current = null;
      return;
    }
    // Reading between its lines: the pane stays, and picks the section up
    // where it was once it writes again.
    if (newest === undefined || !section) return;

    // At the top when it starts, so it writes in below.
    const started = startedAt.current;
    if (started?.root !== root || started.marker !== newest) {
      startedAt.current = { root, marker: newest };
      tail.start();
      sectionTop(section.start).scrollIntoView({ block: "start" });
    }
    tail.follow(section.end);
  }, [root, sections, slots, filling, newest, tail]);

  const scrollTo = (marker: number) => {
    const start = placed.current.get(marker)?.start;
    if (start) sectionTop(start).scrollIntoView({ block: "start" });
  };

  return { state, root, scrollTo } as const;
};
